import type { PropType, VNode } from 'vue';
import type { BlockChildRenderContext } from '#layers/block-engine/utils/block/child-render-context';
import { defineComponent, h, inject, provide } from 'vue';
import { blockChildKey } from '#layers/block-engine/utils/block/api/context';
import { blockChildRenderContextKey } from '#layers/block-engine/utils/block/child-render-context';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';
import { useBlockRenderProps } from '#layers/block-engine/utils/block/props';

const BlockChildRender = defineComponent({
	props: {
		childId: {
			type: String,
			required: true,
		},
		context: {
			type: Object as PropType<BlockChildRenderContext>,
		},
	},
	setup(props, { slots }) {
		const resolvedChildRenderOptions = props.context ?? inject(blockChildRenderContextKey);

		if (!resolvedChildRenderOptions) {
			throw new Error('BlockCustomChildRenderContext not provided');
		}

		const childRenderOptions: BlockChildRenderContext = resolvedChildRenderOptions;

		const getChild = () => childRenderOptions.block.children?.[props.childId];

		provide(blockChildKey, () => {
			const child = getChild();

			if (!child || !isBlockSerializedChild(child)) {
				throw new Error(`Child with id ${props.childId} is unavailable`);
			}

			return child;
		});

		const { getProps } = useBlockRenderProps({
			variables: childRenderOptions.variables,
			variableIdToName: childRenderOptions.variableIdToName,
			getRootContext: childRenderOptions.getRootContext,
			emit: childRenderOptions.emit,
			unwrapBlockEvents: childRenderOptions.unwrapBlockEvents,
		});

		/** Renders a serialized descendant while preserving its own named slots. */
		function renderSerializedChild(childId: string, args: any[]): VNode[] {
			const child = childRenderOptions.block.children?.[childId];

			if (isBlockSerializedChild(child)) {
				return [h(BlockChildRender, {
					childId,
					context: childRenderOptions,
				})];
			}

			if (isBlockSerializedSlotOutlet(child)) {
				return childRenderOptions.rootSlots[child.slotName]?.(...args) ?? [];
			}

			return [];
		}

		/** Builds current explicit and serialized slots for the child block. */
		function getChildSlots() {
			const child = getChild();
			const childSlots: Record<string, (...args: any[]) => VNode[]> = {};

			for (const name of Object.keys(slots)) {
				childSlots[name] = (...args: any[]) => slots[name]?.(...args) ?? [];
			}

			if (!child || !isBlockSerializedChild(child)) {
				return childSlots;
			}

			for (const [name, childIds] of Object.entries(child.slots)) {
				if (name in slots) {
					continue;
				}

				childSlots[name] = (...args: any[]) => childIds
					.flatMap((childId) => renderSerializedChild(childId, args));
			}

			return childSlots;
		}

		return () => {
			const child = getChild();

			if (!child) {
				return h('div', { style: { color: 'red' } }, `Child with id ${props.childId} not found in block ${childRenderOptions.block.id}`);
			}

			if (!isBlockSerializedChild(child)) {
				return h('div', { style: { color: 'red' } }, `Child with id ${props.childId} is not a serialized child`);
			}

			const blockComponent = childRenderOptions.blockRenderComponents[child.blockId];

			if (!blockComponent) {
				return h('div', { style: { color: 'red' } }, `Block component with id ${child.blockId} not found`);
			}

			const childBlock = childRenderOptions.childrenBlocks[child.blockId];
			const childProps = getProps({
				child,
				childBlock,
			});

			return h(blockComponent, childProps, getChildSlots());
		};
	},
});

export default BlockChildRender;
